import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import useTranslation from 'hooks/use-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import useStores from 'shared/hooks/use-stores';
import useApolloClient from 'shared/hooks/use-apollo-client';
import { submitOrRefreshRewardsPin } from 'src/state/actions/cart';
import { CheckoutAccordion, CheckoutAccordionActions, CheckoutAccordionSummary } from 'shared/components';

import {
  CheckoutBody,
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { mediaQueries } from 'shared/styles';
import { RewardsConnectionStatuses } from 'shared/constants';
import { RewardsConnectReadySummary } from 'checkout/components/sections/rewards/connect/ready-summary';
import { RewardsConnectSuccessSummary } from 'checkout/components/sections/rewards/connect/success-summary';
import RewardsPinInput from 'checkout/components/sections/rewards/connect/pin-input';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

const { failed, processing, ready, successful } = RewardsConnectionStatuses;

function RewardsConnect({ status, setRewardsStatus }) {
  const { t } = useTranslation();
  const { Cart, User, UI } = useStores();
  const [isOpen, setIsOpen] = useState(false);
  const apolloClient = useApolloClient();

  const isKioskProEnabled = useKioskPro();
  const largeDevice = useMediaQuery(mediaQueries.largePhone);
  let collapsedSize = largeDevice ? `216px` : `92px`;
  if (isOpen) {
    collapsedSize = null;
  }

  const { dispensary, isGuestOrder, guestCustomer } = Cart.order;
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const phoneNumber = isGuestOrder ? guestCustomer?.phone : User?.profile?.phone;
  const phoneLastFour = phoneNumber?.substr(-4) || '';
  let pinFieldRef;

  function onToggle() {
    if (isOpen) {
      pinFieldRef.clear();
    }

    setIsOpen(!isOpen);

    if (status === failed) {
      setRewardsStatus(ready);
    }
  }

  async function onComplete(pin) {
    setRewardsStatus(processing);
    Cart.rewardPin = pin;

    const success = await submitOrRefreshRewardsPin({ Cart, User, apolloClient, isShowLoyaltyFlagEnabled });
    if (!success) {
      UI.showErnie(
        t('ernie.rewardsPinUnsuccessful', 'We were unable to submit your pin. Please try again later. '),
        `danger`
      );
      setRewardsStatus(ready);
    }
  }

  async function onResendCode() {
    pinFieldRef.clear();
    setRewardsStatus(ready);
    Cart.rewardPin = '';

    const success = await submitOrRefreshRewardsPin({ Cart, User, apolloClient });
    if (!success) {
      UI.showErnie(
        t('ernie.rewardsNewPinUnsuccessful', 'We were unable to send a new pin. Please try again later. '),
        `danger`
      );
      setRewardsStatus(ready);
    }
  }

  return (
    <CheckoutLabelGrouping id='rewardsConnect'>
      <CheckoutLabel>
        <CheckoutSectionHeading />
      </CheckoutLabel>
      <CheckoutBody>
        <StyledCheckoutAccordion
          collapsedSize={collapsedSize}
          dataCyName='rewards-connect'
          expanded={isOpen}
          onCancel={onToggle}
          onExpand={onToggle}
          defaultButton={false}
          hideToggles={!isOpen || status === processing}
          readyState={status === ready}
          cancelText={status === successful ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
          crossfade
        >
          {/* Open state needs a blank summary for Cancel button  */}
          {isOpen && <CheckoutAccordionSummary />}
          {!isOpen && status !== successful && (
            <RewardsConnectReadySummary
              onResendPin={onResendCode}
              onClick={onToggle}
              rewardsProgramDisplayName={Cart.rewardsProgramDisplayName}
              rewardsProgramDisplayDescription={Cart.rewardsProgramDisplayDescription}
            />
          )}
          {!isOpen && status === successful && <RewardsConnectSuccessSummary />}
          <CheckoutAccordionActions>
            <RewardsPinInput
              dispensaryName={dispensary.name}
              onResendCode={onResendCode}
              onComplete={onComplete}
              phoneLastFour={phoneLastFour}
              ref={(p) => (pinFieldRef = p)}
              status={status}
              rewardsProgramDisplayName={Cart.rewardsProgramDisplayName}
            />
          </CheckoutAccordionActions>
        </StyledCheckoutAccordion>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(forwardRef(RewardsConnect));

const StyledCheckoutAccordion = styled(CheckoutAccordion)`
  &.MuiAccordion-root {
    ${({ readyState }) => readyState && `min-height: 92px`};

    @media ${mediaQueries.largePhone} {
      ${({ readyState }) => readyState && `min-height: 240px`};
    }
  }

  .summaryHider {
    @media ${mediaQueries.largePhone} {
      display: block;
    }
  }
`;
